import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface BOM {
  id: string;
  productId: string;
  product?: { id: string; sku: string; name: string };
  bomType: 'ENGINEERING' | 'MANUFACTURING' | 'PLANNING' | 'COSTING';
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'OBSOLETE';
  revision: string;
  effectiveDate?: string;
  expiryDate?: string;
  standardCost?: number;
  components?: BOMComponent[];
}

export interface BOMComponent {
  id: string;
  bomId: string;
  componentProductId: string;
  componentProduct?: { id: string; sku: string; name: string };
  quantityPer: number;
  uom?: string;
  isPhantom: boolean;
  position?: number;
  wastagePercent?: number;
  componentCost?: number;
}

// ============================================================================
// BOM Service
// ============================================================================

export const bomService = {
  // CRUD
  async getBOMs(params?: {
    productId?: string;
    bomType?: string;
    status?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    filters?: string;
  }) {
    const response = await apiClient.get('/manufacturing/bom', { params });
    return response.data;
  },

  async createBOM(dto: {
    productId: string;
    bomType: string;
    revision?: string;
    effectiveDate?: string;
    expiryDate?: string;
    notes?: string;
    components?: Array<{
      componentProductId: string;
      quantityPer: number;
      uom?: string;
      isPhantom?: boolean;
      position?: number;
      wastagePercent?: number;
    }>;
  }) {
    const response = await apiClient.post('/manufacturing/bom', dto);
    return response.data;
  },

  async updateBOMStatus(bomId: string, status: string) {
    const response = await apiClient.put(`/manufacturing/bom/${bomId}/status`, { status });
    return response.data;
  },

  // Components
  async addComponent(
    bomId: string,
    dto: {
      componentProductId: string;
      quantityPer: number;
      uom?: string;
      isPhantom?: boolean;
      position?: number;
      wastagePercent?: number;
    },
  ) {
    const response = await apiClient.post(`/manufacturing/bom/${bomId}/components`, dto);
    return response.data;
  },

  async removeComponent(componentId: string) {
    await apiClient.delete(`/manufacturing/bom/components/${componentId}`);
  },

  // Advanced Operations
  async explodeBOM(bomId: string, levels?: number, includePhantoms?: boolean) {
    const response = await apiClient.post(`/manufacturing/bom/${bomId}/explode`, {
      levels,
      includePhantoms,
    });
    return response.data;
  },

  async getWhereUsed(productId: string, levels?: number) {
    const response = await apiClient.get(`/manufacturing/bom/where-used/${productId}`, {
      params: { levels },
    });
    return response.data;
  },

  async copyBOM(
    bomId: string,
    dto: {
      targetProductId: string;
      newRevision?: string;
      copyComponents?: boolean;
    },
  ) {
    const response = await apiClient.post(`/manufacturing/bom/${bomId}/copy`, dto);
    return response.data;
  },

  // Alias for hook compatibility: get components from BOM detail
};

export default bomService;
